/**
 * PasswordField — password input with live client-side policy validation.
 *
 * Props:
 *  - value, onChange  (controlled)
 *  - id, label, placeholder, autoFocus, disabled, autoComplete
 *  - onValidityChange(bool)   called whenever overall validity changes
 *  - showChecklist (default true)
 */
import React, { useEffect, useMemo, useState } from 'react';

// Mirrors backend rules exactly (core/password_policy.py)
const RULES = [
  { id: 'min_length', label: 'At least 10 characters', test: (p) => p.length >= 10 },
  { id: 'uppercase',  label: 'At least 1 uppercase letter (A–Z)', test: (p) => /[A-Z]/.test(p) },
  { id: 'lowercase',  label: 'At least 1 lowercase letter (a–z)', test: (p) => /[a-z]/.test(p) },
  { id: 'two_digits', label: 'At least 2 numbers', test: (p) => (p.match(/\d/g) || []).length >= 2 },
  { id: 'symbol',     label: 'At least 1 symbol (e.g. ! @ # $)', test: (p) => /[^A-Za-z0-9\s]/.test(p) },
  {
    id: 'no_repeats',
    label: 'No more than 3 identical characters in a row',
    test: (p) => {
      if (p.length < 4) return true;
      let run = 1;
      for (let i = 1; i < p.length; i++) {
        if (p[i] === p[i - 1]) { run += 1; if (run >= 4) return false; }
        else run = 1;
      }
      return true;
    },
  },
];

export function checkPasswordClient(value) {
  const password = value || '';
  const rule_status = {};
  for (const r of RULES) rule_status[r.id] = r.test(password);
  const ok = RULES.every((r) => rule_status[r.id]);
  return { ok, rule_status };
}

export function PasswordField({
  value,
  onChange,
  id = 'password',
  label = 'Password',
  placeholder = 'Choose a strong password',
  autoFocus = false,
  disabled = false,
  autoComplete = 'new-password',
  onValidityChange,
  showChecklist = true,
  testIdPrefix = 'password',
}) {
  const [touched, setTouched] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const { ok, rule_status } = useMemo(() => checkPasswordClient(value), [value]);

  useEffect(() => {
    if (typeof onValidityChange === 'function') onValidityChange(ok);
  }, [ok, onValidityChange]);

  return (
    <div className="form-group">
      {label && <label htmlFor={id}>{label}</label>}
      <div style={{ position: 'relative' }}>
        <input
          id={id}
          type={showPw ? 'text' : 'password'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onBlur={() => setTouched(true)}
          placeholder={placeholder}
          autoFocus={autoFocus}
          disabled={disabled}
          autoComplete={autoComplete}
          data-testid={`${testIdPrefix}-input`}
          style={{ width: '100%', paddingRight: 60 }}
        />
        <button
          type="button"
          onClick={() => setShowPw((s) => !s)}
          data-testid={`${testIdPrefix}-toggle`}
          style={{
            position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)',
            background: 'transparent', border: 0, color: 'var(--text-secondary)',
            fontSize: 11, letterSpacing: '0.04em', cursor: 'pointer', padding: '4px 6px',
          }}
          tabIndex={-1}
        >
          {showPw ? 'HIDE' : 'SHOW'}
        </button>
      </div>

      {showChecklist && (value || touched) && (
        <ul
          data-testid={`${testIdPrefix}-checklist`}
          style={{
            listStyle: 'none', padding: '8px 12px', margin: '8px 0 0',
            fontSize: 11, borderRadius: 6, background: 'var(--bg)',
            border: '1px solid var(--border)',
          }}
        >
          {RULES.map((r) => {
            const passed = rule_status[r.id];
            return (
              <li
                key={r.id}
                data-testid={`${testIdPrefix}-rule-${r.id}`}
                data-status={passed ? 'pass' : 'fail'}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  color: passed ? 'var(--success, #16a34a)' : 'var(--text-secondary)',
                  lineHeight: 1.6,
                }}
              >
                <span style={{
                  display: 'inline-block', width: 14, textAlign: 'center', fontWeight: 700,
                }}>{passed ? '✓' : '·'}</span>
                <span>{r.label}</span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export default PasswordField;
